import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  //   Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  CardContent,
  TablePagination,
  SelectChangeEvent,
} from "@mui/material";
import { Grid } from "@material-ui/core";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import adminServices from "../../services/adminServices";
import { getReImagingDashboardData } from "../../types/adminSection";
import checkBroken from "../../assets/images/adminSection/check-broken.svg";
import Delegate from "../../assets/images/adminSection/delegation.svg";
import clockForward from "../../assets/images/adminSection/clock-forward.svg";
import fileAnalytics from "../../assets/images/adminSection/file-analytics.svg";
import circleContained from "../../assets/images/adminSection/x-circle-contained.svg";
import filter from "../../assets/images/adminSection/filter.svg";
import download from "../../assets/images/adminSection/download-02.svg";
import CustomDatePicker from "../../components/common/customDatePicker";
import CustomSelect from "../../components/common/customSelect";
import dayjs from "dayjs";
import { ReimagineStatusEnum } from "../../helper/typeEnum";
import AutoCompleteUserSelect, {
  UserOption,
} from "../../components/AutoCompleteUserSelect/AutoCompleteUserSelect";
import { showToast } from "../../components/common/toastNotification";
import { REImagingUpdateData } from "../../types/dashboard";
import dashboardService from "../../services/DashboardServices";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import DoDisturbOnOutlinedIcon from "@mui/icons-material/DoDisturbOnOutlined";
import IdeaCapturingModalPopUp from "../successPopUp/ideaCapturePopUp";
import toast from "react-hot-toast";
import { useAppSelector } from "../../globalState/hooks";
import { LoginRootState } from "../../types/login";

interface CardItem {
  label: string;
  count: number;
  icon: string;
}

export interface RewardNominationMember {
  nominationId: number;
  userId: string;
  employeeName: string;
  photoUrl: string;
}

export interface RewardRow {
  id: number;
  awardType: string;
  categoryType: string;
  status: number;
  statusName: string;
  createdBy: string;
  createdByName: string;
  isInitiated: boolean;
  createdAt: string;
}

const getStatusIcon = (status: number) => {
  switch (status) {
    case 2:
      return <CheckCircleOutlineOutlinedIcon sx={{ color: "#4CAF50" }} />;
    case 3:
      return <HighlightOffOutlinedIcon sx={{ color: "#F44336" }} />;
    case 4:
      return <HelpOutlineOutlinedIcon sx={{ color: "#FFFFFF" }} />;
    default:
      return <DoDisturbOnOutlinedIcon sx={{ color: "#FFFFFF" }} />;
  }
};

const ReImagineDashboard = () => {
  const navigate = useNavigate();
  const userDetails = useAppSelector(
    (state: LoginRootState) => state.authReducer.loginDetials
  );
  const [cardCount, setCardCount] = useState<CardItem[]>([]);
  const [tableData, setTableData] = useState<RewardRow[]>([]);

  const [selectedRewardType, setSelectedRewardType] = useState(0);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const financialYear = useAppSelector(
    (state) =>
      state.dashboardReducer?.financialYear || { startDate: "", endDate: "" }
  );
  const [formData, setFormData] = useState<getReImagingDashboardData>({
    startDate: "",
    endDate: "",
    status: "",
    categoryType: "",
    recommendedBy: "",
    categoryTypeId: 0,
    awardId: 0,
    gender: "",
    isIdeathon: null,
  });
  const [formErrors, setFormErrors] = useState<
    Partial<getReImagingDashboardData & { general?: string }>
  >({});

  const [statusData, setStatusData] = useState<
    { label: string; value: number | string }[]
  >([]);
  const [isRewardAcceptanceFormOpen, setIsRewardAcceptanceFormOpen] =
    useState(true);
  const [rewardAcceptanceFormData, setRewardAcceptanceFormData] =
    useState<REImagingUpdateData | null>(null);

  // get table list function
  const fetchTableData = useCallback(
    async (filters?: getReImagingDashboardData) => {
      const input: getReImagingDashboardData = {
        pageNo: page + 1,
        pageSize,
        startDate: filters?.startDate || financialYear.startDate,
        endDate: filters?.endDate || financialYear.endDate,
        status: filters?.status ?? null,
        isIdeathon: filters?.isIdeathon ?? null,
        requestedBy: filters?.requestedBy ?? null,
        gender: filters?.gender ?? null,
        approvalId: userDetails.isSPOC ? userDetails.userId : null,
      };

      try {
        const response = await adminServices.getREImagineDashboardData(input);
        setTableData(response.data.data);
      } catch (error) {
        console.error("Failed to fetch table data", error);
        setTableData([]);
      }
    },
    [page, pageSize]
  );
  //   get card data
  const fetchCardData = useCallback(
    async (filters?: getReImagingDashboardData) => {
      const input: getReImagingDashboardData = {
        startDate: filters?.startDate || financialYear.startDate,
        endDate: filters?.endDate || financialYear.endDate,
        status: filters?.status ?? null,
        awardId: filters?.awardId ?? null,
        categoryId: filters?.categoryId ?? null,
        gender: filters?.gender ?? null,
        isIdeathon: filters?.isIdeathon ?? null,
        requestedBy: filters?.requestedBy ?? null,
        approvalId: userDetails.isSPOC ? userDetails.userId : null,
      };

      try {
        const response = await adminServices.getREImagineDashboardCard(input);
        const apiData = response.data.data;
        const CardData: CardItem[] = [
          { label: "All", count: apiData.totaRecord || 0, icon: fileAnalytics },
          {
            label: "Pending",
            count: apiData.totalPending || 0,
            icon: clockForward,
          },
          {
            label: "Rejected",
            count: apiData.totalRejected || 0,
            icon: circleContained,
          },
          {
            label: "Approved",
            count: apiData.totalApproved || 0,
            icon: checkBroken,
          },
          {
            label: "Delegate ",
            count: apiData.totalDelegated || 0,
            icon: Delegate,
          },
        ];
        setCardCount(CardData);
        setTotalCount(apiData.totaRecord || 0);
      } catch (error) {
        console.error("Failed to fetch dashboard summary", error);
        setCardCount([]);
      }
    },
    []
  );
  useEffect(() => {
    fetchCardData();
    fetchTableData();

    const tempStatusType = Object.entries(ReimagineStatusEnum).map(
      ([key, value]) => ({
        label: key,
        value: value,
      })
    );

    setStatusData(tempStatusType);
  }, [fetchCardData, fetchTableData]);

  const handleChange = (
    field: keyof getReImagingDashboardData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    const errors: typeof formErrors = {};

    if (formData.startDate && formData.endDate) {
      if (dayjs(formData.startDate).isAfter(dayjs(formData.endDate))) {
        errors.startDate = "Start date cannot be after end date";
        errors.endDate = "End date must be after start date";
        showToast("Start date cannot be after end date", "error");
      }
    }

    const hasAnyFilter =
      formData.startDate ||
      formData.endDate ||
      formData.status ||
      formData.gender ||
      selectedUser?.id ||
      formData.rewardType ||
      formData.isIdeathon !== null;

    if (!hasAnyFilter) {
      errors.general = "Please select at least one filter to search.";
      showToast("Please select at least one filter to search.", "error");
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    const payload: getReImagingDashboardData = {
      pageNo: page + 1,
      pageSize: pageSize,
      startDate: formData.startDate || financialYear.startDate,
      endDate: formData.endDate || financialYear.endDate,
      status: formData.status !== "" ? formData.status : null,
      awardId: selectedRewardType !== 0 ? Number(selectedRewardType) : null,
      categoryId:
        formData.categoryTypeId !== 0 ? Number(formData.categoryTypeId) : null,
      requestedBy: selectedUser?.id || null,
      gender: formData.gender || null,
      isIdeathon: formData.isIdeathon,
    };

    fetchTableData(payload);
    fetchCardData(payload);
  };

  const handleTypeDropDownChange = (
    event: SelectChangeEvent<string | number>
  ) => {
    const { value, name } = event.target;
    let newValue: string | number | boolean | null = value;

    if (name === "isIdeathon") {
      if (value === "true") {
        newValue = true;
      } else if (value === "false") {
        newValue = false;
      } else {
        newValue = null;
      }
    }

    if (name === "rewardType") {
      const newRewardType = Number(value);
      setSelectedRewardType(newRewardType);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      categoryType: "",
      awardType: "",
      crossFunctionalType: "",
      ...(name === "categoryTypeId" && { userIds: "" }),
    }));
  };

  //   download excel function

  const handleExportExcel = async () => {
    const defaultStartDate = financialYear.startDate;
    const defaultEndDate = financialYear.endDate;

    const exportPayload: getReImagingDashboardData = {
      startDate: formData.startDate || defaultStartDate,
      endDate: formData.endDate || defaultEndDate,
      status: formData.status !== "" ? formData.status : null,
      isIdeathon: formData.isIdeathon,
      awardId: selectedRewardType !== 0 ? selectedRewardType : null,
      categoryId:
        formData.categoryTypeId !== 0 ? Number(formData.categoryTypeId) : null,
      requestedBy: selectedUser?.id || null,
      gender: formData.gender || null,
      approvalId: userDetails.isSPOC ? userDetails.userId : null,
    };

    try {
      const response = await adminServices.ReImagineDashboardExport(
        exportPayload
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileURL = window.URL.createObjectURL(blob);
      const fileLink = document.createElement("a");

      fileLink.href = fileURL;
      fileLink.setAttribute("download", "ReImagine_Dashboard_Export.xlsx");

      document.body.appendChild(fileLink);
      fileLink.click();
      document.body.removeChild(fileLink);
      toast.success("Excel file exported successfully.");
    } catch (error) {
      console.error("Excel export failed.", error);
    }
  };

  const fetchRewardAndRecognitionStatus = async (id: number) => {
    setIsRewardAcceptanceFormOpen(true);

    try {
      const response =
        await dashboardService.GetREImagineAceecsApproveRejectAPI(id);
      const finalResponse = response.data;
      if (finalResponse.success) {
        setRewardAcceptanceFormData(finalResponse.data);
      }
    } catch (error) {
      console.error("Error fetching reward status:", error);
    }
  };

  return (
    <>
      <Box p={3}>
        {/* Card Summary Section */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          {cardCount.map((item) => (
            <Box
              key={item.label}
              sx={{
                flex: {
                  xs: "0 0 100%",
                  sm: "0 0 48%",
                  md: "0 0 18.5%",
                  lg: "0 0 18.5%",
                },
              }}
            >
              <Card
                sx={{
                  backgroundColor: "#0A0A0A",
                  color: "#fff",
                  p: 2,
                  position: "relative",
                  minHeight: 100,
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  mt={2}
                >
                  <Typography className="f-16" sx={{ mb: 1 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="h4">{item.count}</Typography>
                </Box>
                <Box sx={{ position: "absolute", top: 40, right: 16 }}>
                  <img src={item.icon} alt="img" />
                </Box>
              </Card>
            </Box>
          ))}
        </Box>

        {/* Table and Filter/Download Buttons */}
        <Card
          sx={{
            backgroundColor: "#212121",
            mb: 2,
            borderRadius: 0,
            boxShadow: "none",
          }}
        >
          <CardContent>
            <Grid
              container
              style={{
                marginBottom: 8,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
                RE Imagine
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  className="filterBtn f-14"
                  style={{
                    borderRadius: 0,
                    background: "#212121",
                    minWidth: 140,
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "0 15px",
                  }}
                  onClick={() => setShowFilters((prev) => !prev)}
                >
                  <Typography>Filter</Typography>
                  <img src={filter} alt="filter" />
                </Button>

                <Button
                  className="downloadBtn f-14"
                  variant="contained"
                  style={{
                    borderRadius: 0,
                    background: "#212121",
                    minWidth: 140,
                    display: "flex",
                    justifyContent: "space-between",
                    marginLeft: 20,
                  }}
                  onClick={handleExportExcel}
                >
                  <Typography>Download</Typography>
                  <Box>
                    <img
                      src={download}
                      width={20}
                      style={{ marginTop: 7 }}
                      alt="download"
                    />
                  </Box>
                </Button>
              </Box>
            </Grid>
            {showFilters && (
              <Box
                sx={{
                  backgroundColor: "#5D5D5D",
                  padding: 2,
                  borderRadius: "4px",
                  marginTop: 2,
                }}
              >
                <Grid
                  container
                  spacing={2}
                  style={{ backgroundColor: "#5D5D5D", padding: 2 }}
                >
                  <Grid item xs={12} md={2}>
                    <CustomDatePicker
                      name="startDate"
                      label="From Date"
                      backgroundColor="#5D5D5D"
                      placeholder="Select"
                      value={
                        formData.startDate ? dayjs(formData.startDate) : null
                      }
                      onChange={(date) =>
                        handleChange(
                          "startDate",
                          date ? date.format("YYYY-MM-DD") : ""
                        )
                      }
                      shouldDisableDate={() => false}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <CustomDatePicker
                      name="endDate"
                      label="To Date"
                      backgroundColor="#5D5D5D"
                      placeholder="Select"
                      value={formData.endDate ? dayjs(formData.endDate) : null}
                      onChange={(date) =>
                        handleChange(
                          "endDate",
                          date ? date.format("YYYY-MM-DD") : ""
                        )
                      }
                      shouldDisableDate={() => false}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CustomSelect
                      name="isIdeathon"
                      label="Initiative Source"
                      backgroundColor="#5D5D5D"
                      placeHolder="Select Initiative"
                      value={
                        formData.isIdeathon === true
                          ? "true"
                          : formData.isIdeathon === false
                          ? "false"
                          : ""
                      }
                      onChange={handleTypeDropDownChange}
                      options={[
                        { label: "ReImagine", value: "false" },
                        { label: "Ideathon", value: "true" },
                      ]}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <CustomSelect
                      name="status"
                      backgroundColor="#5D5D5D"
                      label="Status"
                      placeHolder="Select Status"
                      value={formData.status ?? ""}
                      onChange={handleTypeDropDownChange}
                      options={statusData}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <AutoCompleteUserSelect
                      label="Recommended By"
                      placeholder="Recommended By"
                      value={selectedUser}
                      backgroundColor="#5D5D5D"
                      onChange={(user) => {
                        setSelectedUser(user);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CustomSelect
                      name="gender"
                      label="Gender"
                      backgroundColor="#5D5D5D"
                      placeHolder="Select Gender"
                      value={formData.gender || ""}
                      onChange={handleTypeDropDownChange}
                      options={[
                        { label: "Male", value: "Male" },
                        { label: "Female", value: "Female" },
                      ]}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      marginTop: 10,
                    }}
                  >
                    <Button
                      className="otherButton f-14"
                      variant="contained"
                      style={{
                        borderRadius: 0,
                        backgroundColor: "#5D5D5D",
                        width: 140,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: 20,
                      }}
                      onClick={handleSearch}
                    >
                      Search
                    </Button>
                    <Button
                      className="otherButton f-14"
                      variant="contained"
                      style={{
                        borderRadius: 0,
                        backgroundColor: "#5D5D5D",
                        width: 140,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: 20,
                      }}
                      onClick={() => {
                        fetchTableData();
                        fetchCardData();
                        setFormData({
                          startDate: "",
                          endDate: "",
                          isIdeathon: null,
                          status: null,
                          gender: null,
                        });
                        setSelectedUser(null);
                      }}
                    >
                      Clear
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Table Section */}
            <TableContainer
              sx={{
                backgroundColor: "#212121",
                padding: 0,
                minHeight: "300px",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      "Process For",
                      "Process Initiation",
                      "Delegate",
                      "Idea Accepted",
                      "Idea Rejected",
                      "Action",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          color: "#fff",
                          background: "#0A0A0A",
                          fontWeight: "600",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{
                          color: "#fff",
                          textAlign: "center",
                          alignItems: "center",
                        }}
                      >
                        No data available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ color: "#fff", maxWidth: 100 }}>
                          {row.createdByName} {row.createdBy}
                        </TableCell>

                        <TableCell
                          sx={{
                            color: "#fff",
                            position: "relative",
                            whiteSpace: "nowrap",
                            minWidth: 130,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              position: "relative",
                            }}
                          >
                            <Typography variant="body2" sx={{ mr: 1 }}>
                              {dayjs(row.createdAt).format("YYYY-MM-DD")}
                            </Typography>
                            {row.isInitiated ? (
                              <CheckCircleOutlineOutlinedIcon
                                sx={{ color: "#4CAF50", fontSize: 20 }}
                              />
                            ) : (
                              <HighlightOffOutlinedIcon
                                sx={{ color: "#F44336", fontSize: 20 }}
                              />
                            )}

                            <Box
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "calc(100% - 115px)",
                                width: "130px",
                                height: "2px",
                                display: { xs: "none", md: "block" },
                                backgroundColor:
                                  row.status === 2
                                    ? "#4CAF50"
                                    : row.status === 3
                                    ? "#F44336"
                                    : row.status === 4
                                    ? "#FFFFFF"
                                    : "transparent",
                                zIndex: 1,
                              }}
                            />
                          </Box>
                        </TableCell>

                        <TableCell
                          sx={{
                            textAlign: "left",
                            position: "relative",
                            zIndex: 2,
                            minWidth: 110,
                          }}
                        >
                          {" "}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              position: "relative",
                            }}
                          >
                            {getStatusIcon(row.status)}
                            <Box
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "calc(100% - 125px)",
                                width: "130px",
                                display: { xs: "none", md: "block" },
                                height: "2px",
                                backgroundColor:
                                  row.status === 2
                                    ? "#4CAF50"
                                    : row.status === 3
                                    ? "#F44336"
                                    : row.status === 4
                                    ? "transparent"
                                    : "transparent",
                                zIndex: 1,
                              }}
                            ></Box>
                          </Box>
                        </TableCell>

                        <TableCell
                          sx={{
                            textAlign: "left",
                            position: "relative",
                            zIndex: 2,
                            minWidth: 110,
                          }}
                        >
                          {" "}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              position: "relative",
                            }}
                          >
                            {getStatusIcon(row.status)}
                            <Box
                              sx={{
                                position: "absolute",
                                top: "50%",
                                left: "calc(100% - 125px)",
                                width: "130px",
                                display: { xs: "none", md: "block" },
                                height: "2px",
                                backgroundColor:
                                  row.status === 2
                                    ? "transparent"
                                    : row.status === 3
                                    ? "#F44336"
                                    : row.status === 4
                                    ? "transparent"
                                    : "transparent",
                                zIndex: 1,
                              }}
                            ></Box>
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: "left",
                            position: "relative",
                            zIndex: 2,
                            minWidth: 100,
                          }}
                        >
                          {" "}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              position: "relative",
                            }}
                          >
                            {row.status === 2 ? (
                              <DoDisturbOnOutlinedIcon
                                sx={{ color: "#FFFFFF" }}
                              />
                            ) : (
                              getStatusIcon(row.status)
                            )}
                          </Box>
                        </TableCell>

                        <TableCell>
                          <IconButton
                            onClick={() =>
                              fetchRewardAndRecognitionStatus(row.id)
                            }
                          >
                            <VisibilityIcon sx={{ color: "#fff" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Grid
              style={{
                justifyContent: "flex-end",
                alignItems: "center",
                display: "flex",
                marginTop: 16,
                padding: 8,
              }}
            >
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  color: "#ffffff",
                  "& .MuiSelect-icon": { color: "#ffffff" },
                  "& .MuiSvgIcon-root": { color: "#ffffff" },
                }}
              />
            </Grid>
          </CardContent>
        </Card>
      </Box>
      {/* ------------ Idea capture Form Pop Up ------------------- */}
      {rewardAcceptanceFormData && (
        <IdeaCapturingModalPopUp
          open={isRewardAcceptanceFormOpen}
          onClose={() => {
            setIsRewardAcceptanceFormOpen(false);
            navigate("/ReImagineDashboard");
            fetchCardData();
            fetchTableData();
          }}
          reImagineUpdateData={rewardAcceptanceFormData}
        />
      )}
    </>
  );
};

export default ReImagineDashboard;
